<script>
import lazyLoadComponent from '@router/lazyload-component'
import { genderFilter, orderStatusMap } from '@utils/filter-value-to-text'

const initData = () => {
  return {
    prepaidId: '',
    fullName: '',
    gender: 'female',
    phoneCode: '+886',
    phoneNumber: '',
    email: '',
    note: '',
    status: 2,
    memberId: '',
  }
}
export default {
  components: {
    CountryCodePicker: () =>
      lazyLoadComponent(import('@components/CountryCodePicker')),
    OrderChangeStatusDialog: () =>
      lazyLoadComponent(import('./OrderChangeStatusDialog')),
  },
  filters: {
    genderFilter,
  },
  props: {
    defaultValue: {
      type: Object,
      default: () => initData(),
    },
    prepaid: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      tryingStore: false,
      submitData: Object.assign({}, this.defaultValue),
      searchData: {
        phoneCode: '+886',
        phoneNumber: '',
      },
      searchRules: {
        phoneNumber: [
          {
            validator: (rule, value, callback) => {
              if (!value) return callback()
              this.$store
                .dispatch('member/findByPhone', this.searchData)
                .then((member) => {
                  this.updateMemberInfo(member)
                })
                .catch(() => {
                  this.updateMemberInfo()
                  return callback(new Error('此手機號碼尚未註冊'))
                })
            },
            trigger: 'custom',
          },
        ],
      },
      statusDialogVisible: false,
      memberInfo: '',
    }
  },
  computed: {
    submitRules() {
      const isTaiwan = this.submitData.phoneCode === '+886'
      return {
        phoneNumber: [
          {
            required: true,
            message: '請填寫手機號碼',
            trigger: 'blur',
          },
          {
            pattern: isTaiwan ? /^09\d{8}$/ : '',
            message: '格式請符合 09XXXXXXXX',
            trigger: 'blur',
          },
        ],
      }
    },

    isEditMode() {
      return this.defaultValue.id
    },
    statusIsChanged() {
      return this.defaultValue.status !== this.submitData.status
    },
    filteredStatusMap() {
      // ! 已付款不能手動改成未付款
      const filteredStatus = Object.keys(orderStatusMap)
        .filter((status) => status <= this.defaultValue.status)
        .reduce((obj, key) => {
          obj[key] = orderStatusMap[key]
          return obj
        }, {})

      return this.isEditMode ? filteredStatus : orderStatusMap
    },
  },
  watch: {
    memberInfo: 'updateUserByMember',
  },

  methods: {
    emitCancel() {
      this.$emit('cancel')
    },
    emitStore() {
      this.$emit('store')
    },

    async handlePaidStatus(merchantNote) {
      this.isEditMode
        ? this.tryToSaveOrder(merchantNote)
        : this.tryToCreateOrder(merchantNote)
    },

    handleStoreOrder() {
      // show the status dialog when changing order status
      if (
        this.statusIsChanged &&
        (this.submitData.status === 1 || this.isEditMode)
      )
        return (this.statusDialogVisible = true)
      this.isEditMode ? this.tryToSaveOrder() : this.tryToCreateOrder()
    },
    handleCreateSuccess(requirePay = false) {
      this.$alert(
        `已發送付款${requirePay ? '連結' : '完成信'}至客戶 Email`,
        '預付方案購買成功！',
        {
          confirmButtonText: '確認',
          center: true,
          callback: () => {
            this.emitStore()
          },
        }
      )
    },
    handleSaveSuccess() {
      this.$message({
        message: '編輯成功！',
        type: 'success',
      })
      this.emitStore()
    },
    async tryToSaveOrder(merchantNote = '') {
      const storeData = {
        ...this.submitData,
        merchantNote,
      }
      this.tryingStore = true
      try {
        await this.$store.dispatch('prepaid/storeOrder', storeData)
        this.handleSaveSuccess()
      } catch (error) {
        console.log('TCL: tryToSaveOrder -> error', error)
      } finally {
        this.tryingStore = false
      }
    },
    async tryToCreateOrder(merchantNote = '') {
      const storeData = {
        ...this.submitData,
        prepaidId: this.prepaid.id,
        merchantNote,
      }

      this.tryingStore = true

      try {
        await this.$store.dispatch('prepaid/storeOrder', storeData)
        const requirePay = storeData.status !== 1
        this.handleCreateSuccess(requirePay)
      } catch (error) {
        console.log('TCL: tryToCreateOrder -> error', error)
      } finally {
        this.tryingStore = false
      }
    },

    validPhoneNumber(phoneNumber) {
      this.$refs.searchForm.validateField('phoneNumber')
    },
    updateMemberInfo(member = '') {
      if (!member) {
        this.submitData.phoneNumber = this.searchData.phoneNumber
      }
      this.memberInfo = member
    },
    updateUserByMember() {
      const fields = [
        'memberId',
        'fullName',
        'gender',
        'phoneCode',
        'phoneNumber',
        'email',
      ]
      fields.forEach((field) => {
        this.submitData[field] =
          field === 'memberId' ? this.memberInfo.id : this.memberInfo[field]
      })
    },
  },
}
</script>
<template>
  <div :class="$style.wrapper">
    <el-form
      v-if="!isEditMode"
      ref="searchForm"
      :model="searchData"
      :rules="searchRules"
      label-position="top"
      :class="$style['search-form']"
      class="el-form-item--normal-margin el-form-item--white-bg"
    >
      <el-form-item prop="phoneNumber" label="快速查詢客戶資料">
        <el-col :span="20">
          <el-input
            v-model.trim="searchData.phoneNumber"
            placeholder="請輸入手機號碼"
            @keyup.native.enter="validPhoneNumber"
          >
            <CountryCodePicker slot="prepend" v-model="searchData.phoneCode" />
          </el-input>
        </el-col>
        <el-col :span="4">
          <el-button type="primary" @click="validPhoneNumber">查詢</el-button>
        </el-col>
      </el-form-item>
      <template v-if="memberInfo">
        <h6 :class="$style.title">客戶資訊</h6>
        <ul :class="$style.info">
          <li>{{memberInfo.fullName}} {{memberInfo.gender | genderFilter}}</li>
          <li>{{`${memberInfo.phoneCode} ${memberInfo.phoneNumber}`}}</li>
          <li>{{memberInfo.email}}</li>
        </ul>
        <h6 :class="$style.title">你對此客戶之備註</h6>
        <p>{{memberInfo.merchantNote || '目前尚無備註'}}</p>
      </template>
    </el-form>
    <el-form
      ref="form"
      :model="submitData"
      :rules="submitRules"
      label-position="top"
      class="el-form-item--normal-margin el-form-item--white-bg"
    >
      <el-form-item prop="status" label="訂單狀態">
        <el-select v-model="submitData.status" placeholder="訂單狀態">
          <el-option
            v-for="(status, statusValue) in filteredStatusMap"
            :key="statusValue"
            :value="+statusValue"
            :label="status.text"
          ></el-option>
        </el-select>
      </el-form-item>

      <template v-if="!isEditMode && !memberInfo ">
        <el-form-item prop="fullName" label="客戶資訊">
          <el-input v-model="submitData.fullName" placeholder="姓名">
            <el-select slot="append" v-model="submitData.gender" placeholder="性別">
              <el-option value="female" label="小姐"></el-option>
              <el-option value="male" label="先生"></el-option>
            </el-select>
          </el-input>
        </el-form-item>
        <el-form-item prop="phoneNumber">
          <el-input v-model="submitData.phoneNumber" placeholder="手機號碼" autocomplete="tel">
            <CountryCodePicker slot="prepend" v-model="submitData.phoneCode" />
          </el-input>
        </el-form-item>
        <el-form-item prop="email">
          <el-input v-model="submitData.email" placeholder="Email"></el-input>
        </el-form-item>
      </template>
      <template v-if="isEditMode">
        <el-form-item label="客戶資訊">
          <ul :class="$style.info">
            <li>{{submitData.fullName}} {{submitData.gender | genderFilter}}</li>
            <li>{{ `${submitData.phoneCode} ${submitData.phoneNumber}` }}</li>
            <li>{{submitData.email}}</li>
          </ul>
        </el-form-item>
      </template>
    </el-form>
    <el-row :gutter="20">
      <el-col :span="12">
        <el-button class="is-fullwidth" @click="emitCancel">{{ `取消${(isEditMode) ? '編輯' : '新增'}` }}</el-button>
      </el-col>
      <el-col :span="12">
        <el-button
          class="is-fullwidth"
          type="primary"
          :loading="tryingStore"
          @click="handleStoreOrder"
        >{{ (isEditMode) ? '儲存變更' : '新增訂單'}}</el-button>
      </el-col>
    </el-row>
    <OrderChangeStatusDialog
      :visible.sync="statusDialogVisible"
      :order="submitData"
      @paid="handlePaidStatus"
      @cancelled="emitStore"
    />
  </div>
</template>
<style lang="scss" module>
@import '@design';
.wrapper {
  border-top: 1px solid $color-border-base;
  :global {
    .el-form-item__label {
      @extend %font-heading;
    }
  }
}

.search-form {
  margin-bottom: 15px;
  border-bottom: 1px solid $color-border-base;
}

.title:not(:first-child) {
  margin-top: 30px;
}

.info {
  padding-inline-start: 0;
  line-height: 1.2;
  > li {
    padding: 5px 0;
  }
}

.params {
  &-options {
    display: flex;
    justify-content: space-between;

    &:not(:last-child) {
      margin-bottom: 10px;
    }
  }
  &-list {
    padding: 5px 15px;
    background: white;
    &__item {
      display: flex;
      justify-content: space-between;

      &:first-child {
        color: $color-text-placeholder;
      }
      > * {
        flex: 1;
        &:first-child {
          flex: 2;
        }
        &:not(:first-child) {
          text-align: right;
        }
      }
    }
  }
}
</style>